import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef } from "react";

import AnalyzingProgress from "./AnalyzingProgress";

interface StudioStartPanelProps {
  mode: "parse" | "train";
  busy: boolean;
  isUploading: boolean;
  isExtracting: boolean;
  practiceId: string;
  setPracticeId: (id: string) => void;
  selectedPractices: PracticeMessage[];
  selectedTemplateId: string;
  setSelectedTemplateId: (id: string) => void;
  templates: StudioTemplateMessage[];
  prompt: string;
  setPrompt: (p: string) => void;
  pendingFile: File | null;
  setPendingFile: (f: File | null) => void;
  pendingBlobUrl: string | null;
  sampleBlobUrl: string | null;
  onAnalyze: () => void;
}

export default function StudioStartPanel({
  mode,
  busy,
  isUploading,
  isExtracting,
  practiceId,
  setPracticeId,
  selectedPractices,
  selectedTemplateId,
  setSelectedTemplateId,
  templates,
  prompt,
  setPrompt,
  pendingFile,
  setPendingFile,
  pendingBlobUrl,
  sampleBlobUrl,
  onAnalyze,
}: StudioStartPanelProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  let analyzeLabel = "Analyze Document";
  if (isUploading) analyzeLabel = "Uploading…";
  else if (isExtracting) analyzeLabel = "Analyzing…";

  const idleCard = (
    <Paper
      variant="outlined"
      sx={{ width: "100%", padding: 4, borderRadius: 2 }}
    >
      {busy ? (
        <AnalyzingProgress phase={isUploading ? "uploading" : "extracting"} />
      ) : (
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {mode === "parse" ? "Parse Document" : "Train New Template"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mode === "parse"
                ? "Pick an existing template, or skip and edit the prompt below to parse with a one-off prompt."
                : "Upload a sample document. We'll produce a draft JSON extraction you can refine before saving the template."}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              PRACTICE
            </Typography>
            <Select
              value={practiceId}
              onChange={(e) => setPracticeId(e.target.value)}
              size="small"
              displayEmpty
              fullWidth
              sx={{ marginTop: 0.5 }}
            >
              <MenuItem value="" disabled>
                Select a practice
              </MenuItem>
              {selectedPractices.map((p) => (
                <MenuItem key={p.wieldyId} value={p.wieldyId}>
                  {p.displayName || p.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {mode === "parse" && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                TEMPLATE
              </Typography>
              <Select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                size="small"
                displayEmpty
                fullWidth
                sx={{ marginTop: 0.5 }}
              >
                <MenuItem value="">No template — use prompt below</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {t.sourceName ? ` · ${t.sourceName}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {mode === "parse" && !selectedTemplateId && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                PROMPT
              </Typography>
              <TextField
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                size="small"
                sx={{ marginTop: 0.5 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", marginTop: 0.5 }}
              >
                Default prompt is auto-filled. Tweak if you want, or leave
                as-is.
              </Typography>
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              DOCUMENT
            </Typography>
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                marginTop: 0.5,
                border: "1px dashed",
                borderColor: pendingFile ? "primary.main" : "divider",
                borderRadius: 1,
                paddingX: 1.5,
                paddingY: 1,
                cursor: "pointer",
                backgroundColor: pendingFile ? "#F5F3FF" : "#FAFAFA",
                ":hover": { backgroundColor: "#F5F3FF" },
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minHeight: 0,
              }}
            >
              <UploadFileIcon
                sx={{ fontSize: 20, color: "text.secondary", flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                }}
              >
                {pendingFile ? pendingFile.name : "Click to select a PDF"}
              </Typography>
              {pendingFile && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  {(pendingFile.size / 1024).toFixed(0)} KB
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
            />
          </Box>

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={
              !pendingFile ||
              !practiceId ||
              busy ||
              (mode === "parse" && !selectedTemplateId && !prompt.trim())
            }
            onClick={onAnalyze}
            startIcon={
              busy ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : null
            }
          >
            {mode === "parse" ? "Parse Document" : analyzeLabel}
          </Button>
        </Stack>
      )}
    </Paper>
  );

  const leftPane = (() => {
    if (mode === "parse") {
      if (selectedTemplateId && sampleBlobUrl) {
        return (
          <iframe
            title="Template Sample Preview"
            src={sampleBlobUrl}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        );
      }
      if (!selectedTemplateId && pendingBlobUrl) {
        return (
          <iframe
            title="Selected PDF Preview"
            src={pendingBlobUrl}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        );
      }
      if (!selectedTemplateId && !pendingFile) {
        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              padding: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              Pick a template or select a PDF on the right to preview it here.
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Loading sample…
            </Typography>
          </Stack>
        </Box>
      );
    }

    return pendingBlobUrl ? (
      <iframe
        title="Selected PDF Preview"
        src={pendingBlobUrl}
        style={{ width: "100%", height: "100%", border: 0 }}
      />
    ) : (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          padding: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          Select a PDF on the right to preview it here.
        </Typography>
      </Box>
    );
  })();

  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{ height: "calc(100vh - 64px)", padding: 0 }}
    >
      <Box
        sx={{
          flex: 1,
          backgroundColor: "#F5F5F5",
          position: "relative",
          minWidth: 0,
          borderRight: "1px solid #E0E0E0",
        }}
      >
        {leftPane}
      </Box>
      <Box
        sx={{
          width: 680,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
          paddingX: 3,
          paddingY: 3,
          backgroundColor: "#FAFAFA",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 600, marginY: "auto" }}>
          {idleCard}
        </Box>
      </Box>
    </Stack>
  );
}
